import type { ChecklistEditor } from '~/features/checklists/model'
import type { STDocumentType, STMissingReason } from '@st/sdk'
import { SearchableSelect, Select } from '@st/theme'
import type { ChecklistMessage } from '~/features/checklists/module'
import { checklistMissingReasons, documentRequestCanSave } from '../selectors'
import { ChecklistEditDialog, ChecklistNote } from '~/features/checklists/edit-dialog'

type Props<K extends ChecklistEditor['kind']> = {
  editor: Extract<ChecklistEditor, { kind: K }>
  reasons: Record<string, STMissingReason[]>
  send: (message: ChecklistMessage) => void
  onClose: () => void
}

export function DocumentRequestDialog({
  editor,
  documentTypes,
  reasons,
  onClose,
  send
}: Props<'document_request'> & { documentTypes: STDocumentType[] }) {
  const { target, draft } = editor
  const context = draft.context
  return (
    <ChecklistEditDialog
      editing={target.mode == 'edit'}
      valid={documentRequestCanSave(editor)}
      onSave={() => send({ type: 'saveChecklistDialog' })}
      onClose={onClose}
    >
      <label className="block space-y-2 text-sm text-gray-700">
        <span>Document type</span>
        <SearchableSelect
          title="Document type"
          disabled={target.mode === 'edit'}
          emptyLabel="No document types found"
          placeholder="Select document type"
          value={context.documentTypeId}
          options={documentTypes}
          buildValue={(d) => d.id}
          buildLabel={(d) => d.name}
          onChange={(id) => {
            if (id)
              send({
                type: 'documentRequestChanged',
                documentType: documentTypes.find((documentType) => documentType.id == id)!
              })
          }}
        />
      </label>
      <ChecklistNote
        value={draft.note}
        onChange={(note) => send({ type: 'checklistDraftChanged', patch: { note } })}
      />
      {target.mode == 'edit' && (
        <IntakeIncompleteReason
          options={checklistMissingReasons(context, reasons)}
          value={draft.incompleteReason}
          onChange={(incompleteReason) =>
            send({ type: 'incompleteReasonChanged', value: incompleteReason ?? '' })
          }
        />
      )}
    </ChecklistEditDialog>
  )
}

export function QuestionnaireEntryDialog({
  editor,
  reasons,
  onClose,
  send
}: Props<'questionnaire_entry'>) {
  const {
    target: { item },
    draft
  } = editor
  const context = draft.context
  return (
    <ChecklistEditDialog
      editing
      onClose={onClose}
      onSave={() => send({ type: 'saveChecklistDialog' })}
    >
      <div className="text-base font-medium">{item.name}</div>
      <ChecklistNote
        value={draft.note}
        onChange={(note) => send({ type: 'checklistDraftChanged', patch: { note } })}
      />
      <IntakeIncompleteReason
        options={checklistMissingReasons(context, reasons)}
        value={draft.incompleteReason}
        onChange={(incompleteReason) =>
          send({ type: 'incompleteReasonChanged', value: incompleteReason ?? '' })
        }
      />
    </ChecklistEditDialog>
  )
}

function IntakeIncompleteReason({
  options,
  value,
  onChange
}: {
  options: STMissingReason[]
  value: string | undefined
  onChange: (value: string | undefined) => void
}) {
  if (options.length == 0) return null
  return (
    <label className="flex flex-col gap-2 text-sm text-gray-700">
      Incomplete reason
      <Select
        placeholder="None"
        options={options}
        value={value}
        buildValue={(r) => r.key}
        buildLabel={(r) => r.name}
        onChange={onChange}
      />
    </label>
  )
}
